import { Permission } from '@affine/graphql';
import {
  backoffRetry,
  effect,
  Entity,
  exhaustMapWithTrailing,
  fromPromise,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { type Subscription, tap } from 'rxjs';

import type { WorkspaceService } from '../../workspace';
import type { WorkspacePermissionStore } from '../stores/permission';
import { workspacePermissionEventMayChangeRole } from '../utils/workspace-permission-events';

export class WorkspacePermission extends Entity {
  private readonly permissionsChangedSubscription?: Subscription;

  private readonly cache$ = LiveData.from(
    this.store.watchWorkspacePermissionCache(
      this.workspaceService.workspace.id
    ),
    undefined
  );
  isOwner$ = this.cache$.map(cache => cache?.isOwner ?? null);
  isAdmin$ = this.cache$.map(cache => cache?.isAdmin ?? null);
  role$ = this.cache$.map(cache => cache?.role ?? null);
  isOwnerOrAdmin$ = this.cache$.map(
    cache => (cache?.isOwner ?? null) || (cache?.isAdmin ?? null)
  );
  isTeam$ = this.cache$.map(cache => cache?.isTeam ?? null);
  isRevalidating$ = new LiveData(false);

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly store: WorkspacePermissionStore
  ) {
    super();
    this.permissionsChangedSubscription = this.subscribeToPermissionEvents();
    // Eagerly fetch workspace permissions on entity creation.
    // Without this, isOwner$/isAdmin$ stay null until something external
    // happens to call revalidate(), leaving permission-dependent code
    // (like the role-aware ready gate) stuck in an unknown state.
    this.revalidate();
  }

  private subscribeToPermissionEvents() {
    const permissionsChanged$ =
      this.workspaceService.workspace.engine.permissionsChanged$;
    if (!permissionsChanged$) {
      return undefined;
    }

    return permissionsChanged$.subscribe(event => {
      if (workspacePermissionEventMayChangeRole(event.changeType)) {
        this.revalidate();
      }
    });
  }

  revalidate = effect(
    exhaustMapWithTrailing(() => {
      return fromPromise(async signal => {
        if (
          this.workspaceService.workspace.flavour !== 'local' &&
          !this.workspaceService.workspace.openOptions.isSharedMode
        ) {
          const info = await this.store.fetchWorkspaceInfo(
            this.workspaceService.workspace.id,
            signal
          );

          return {
            isOwner: info.workspace.role === Permission.Owner,
            isAdmin: info.workspace.role === Permission.Admin,
            isTeam: info.workspace.team,
            role: info.workspace.role,
          };
        } else {
          return {
            isOwner: true,
            isAdmin: false,
            isTeam: false,
            role: Permission.Owner,
          };
        }
      }).pipe(
        backoffRetry({
          count: Infinity,
        }),
        tap(({ isOwner, isAdmin, isTeam, role }) => {
          this.store.setWorkspacePermissionCache(
            this.workspaceService.workspace.id,
            {
              isOwner,
              isAdmin,
              isTeam,
              role,
            }
          );
        }),
        onStart(() => this.isRevalidating$.setValue(true)),
        onComplete(() => this.isRevalidating$.setValue(false))
      );
    })
  );

  async waitForRevalidation(signal?: AbortSignal) {
    // Set synchronously BEFORE triggering the async pipeline.
    // exhaustMapWithTrailing uses asyncScheduler — without this,
    // waitFor resolves immediately before the fetch starts.
    this.isRevalidating$.setValue(true);
    this.revalidate();
    await this.isRevalidating$.waitFor(
      isRevalidating => !isRevalidating,
      signal
    );
  }

  override dispose(): void {
    this.permissionsChangedSubscription?.unsubscribe();
    this.revalidate.unsubscribe();
  }
}
